import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { NotePencil, PaperPlaneTilt, CheckCircle, Clock, Sparkle } from "@phosphor-icons/react";
import { SendChecklistDialog } from "@/components/SendChecklistDialog";
import { toast } from "sonner";

function todayISO() { return new Date().toISOString().slice(0, 10); }

const START_HOUR = 8;
const END_HOUR = 19; // last slot ends at 19:00
function pad(n) { return String(n).padStart(2, "0"); }
const SLOTS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => {
  const h = START_HOUR + i;
  return `${pad(h)}:00 – ${pad(h + 1)}:00`;
});

const COLORS = [
  { accent: "bg-primary/60", chip: "bg-secondary text-primary", fill: "bg-secondary/50", hover: "hover:border-primary/25" },
];

export default function SubmitReport() {
  const navigate = useNavigate();
  const [reportDate, setReportDate] = useState(todayISO());
  const [summary, setSummary] = useState("");
  const [entries, setEntries] = useState({});
  const [saving, setSaving] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [existingId, setExistingId] = useState(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const BLANK = { work: "", challenges: "", suggestions: "" };
  const getEntry = (slot) => entries[slot] || BLANK;
  const setField = (slot, field, val) =>
    setEntries((prev) => ({ ...prev, [slot]: { ...BLANK, ...prev[slot], [field]: val } }));
  const isFilled = (e) => !!(e.work.trim() || e.challenges.trim() || e.suggestions.trim());

  const collectLogs = () =>
    SLOTS.map((s) => ({ hour: s, text: getEntry(s).work, challenges: getEntry(s).challenges, suggestions: getEntry(s).suggestions }))
      .filter((h) => h.text.trim() || h.challenges.trim() || h.suggestions.trim());

  const summarizeDay = async () => {
    const hour_logs = collectLogs();
    if (hour_logs.length === 0) { toast.error("Add at least one hourly log first"); return; }
    setSummarizing(true);
    try {
      const { data } = await api.post("/reports/summarize", { report_date: reportDate, hour_logs });
      setSummary(data.summary);
      toast.success("Summary generated — edit it if you'd like");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSummarizing(false);
    }
  };

  const applyReport = (data) => {
    setReportDate(data.report_date);
    setSummary(data.summary || "");
    setExistingId(data.id);
    const map = {};
    (data.hour_logs || []).forEach((h) => {
      map[h.hour] = { work: h.text || "", challenges: h.challenges || "", suggestions: h.suggestions || "" };
    });
    setEntries(map);
  };

  useEffect(() => {
    api.get("/reports/today").then(({ data }) => { if (data) applyReport(data); });
  }, []);

  const loadForDate = async (d) => {
    setReportDate(d);
    try {
      const { data } = await api.get("/reports");
      const match = data.find((r) => r.report_date === d);
      if (match) applyReport(match);
      else { setExistingId(null); setSummary(""); setEntries({}); }
    } catch (e) {}
  };

  const filledCount = SLOTS.filter((s) => isFilled(getEntry(s))).length;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const hour_logs = collectLogs();
      const { data } = await api.post("/reports", { report_date: reportDate, summary, hour_logs });
      applyReport(data);
      toast.success(existingId ? "Report updated" : "Report submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!existingId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/reports/${existingId}/send-email`);
      toast.success(`${data.message}${data.mocked ? " (mock)" : ""}`);
      setChecklistOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Daily Timesheet</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight mt-1">
          <span className="gradient-text">{existingId ? "Today's Timesheet" : "Log Your Work Hours"}</span>
        </h1>
      </div>

      {existingId && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-4 py-2.5">
          <CheckCircle size={18} weight="fill" /> You already submitted a report for this date. Edits will update it.
        </div>
      )}

      <Card className="p-6 sm:p-8 rounded-md border-border">
        <form onSubmit={submit} className="space-y-6">
          <div className="max-w-xs">
            <Label className="text-xs font-medium">Date</Label>
            <Input data-testid="report-date-input" type="date" value={reportDate}
              onChange={(e) => loadForDate(e.target.value)} className="mt-1.5" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 text-primary"><Clock size={15} weight="fill" /> Hourly Log</Label>
              <span className="text-xs font-mono-num font-medium px-2.5 py-1 rounded-full bg-secondary text-primary">{filledCount}/{SLOTS.length} slots</span>
            </div>
            <div className="hidden sm:grid grid-cols-[7rem_1fr_1fr_1fr] gap-2 px-2 pb-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Time</span>
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Work done</span>
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Challenges</span>
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Suggestions</span>
            </div>
            <div className="space-y-2">
              {SLOTS.map((slot, i) => {
                const c = COLORS[i % COLORS.length];
                const e = getEntry(slot);
                const filled = isFilled(e);
                const inputCls = "w-full px-2 py-2 text-sm bg-transparent rounded-md outline-none focus:bg-white/70 placeholder:text-muted-foreground/40 sm:placeholder:opacity-0 sm:focus:placeholder:opacity-100";
                return (
                  <div key={slot}
                    className={`rounded-xl border p-2 transition-all duration-200 ${filled ? `${c.fill} border-transparent shadow-sm` : `bg-white border-border ${c.hover}`}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-[7rem_1fr_1fr_1fr] gap-1.5 sm:gap-2 sm:items-center">
                      <div className="flex items-center gap-2">
                        <span className={`h-6 w-1.5 rounded-full transition-colors duration-200 ${filled ? c.accent : "bg-slate-200"}`} />
                        <span className={`text-[11px] font-mono-num font-medium px-2 py-1 rounded-lg ${c.chip}`}>{slot}</span>
                      </div>
                      <input data-testid={`hour-input-${slot.slice(0, 5)}`} value={e.work}
                        onChange={(ev) => setField(slot, "work", ev.target.value)} placeholder="Work done" className={inputCls} />
                      <input data-testid={`challenges-input-${slot.slice(0, 5)}`} value={e.challenges}
                        onChange={(ev) => setField(slot, "challenges", ev.target.value)} placeholder="Challenges" className={inputCls} />
                      <input data-testid={`suggestions-input-${slot.slice(0, 5)}`} value={e.suggestions}
                        onChange={(ev) => setField(slot, "suggestions", ev.target.value)} placeholder="Suggestions" className={inputCls} />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Fill in only the hours you worked — empty rows are skipped. Challenges & suggestions are optional.</p>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <Label className="text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 text-primary"><Sparkle size={15} weight="fill" /> Day Summary</Label>
              <Button type="button" data-testid="summarize-day-btn" onClick={summarizeDay} disabled={summarizing}
                variant="outline" size="sm" className="rounded-full border-primary/30 text-primary hover:bg-primary/5">
                {summarizing ? (
                  <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" /> Summarizing…</span>
                ) : (
                  <span className="flex items-center gap-1.5"><Sparkle size={16} weight="fill" /> Summarize Day with AI</span>
                )}
              </Button>
            </div>
            <Textarea data-testid="report-summary-input" value={summary} rows={5} required
              onChange={(e) => setSummary(e.target.value)} className="resize-none"
              placeholder="Click 'Summarize Day with AI' to generate a summary from your hourly log — or write your own. You can edit the generated text." />
            <p className="mt-2 text-xs text-muted-foreground">This summary is used as the email body when you send the report. The hourly log is attached as a PDF.</p>
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <Button type="submit" data-testid="save-report-btn" disabled={saving}
              className="rounded-md bg-primary hover:bg-primary/90 transition-transform duration-200 hover:-translate-y-[1px]">
              <NotePencil size={18} className="mr-1.5" /> {saving ? "Saving…" : existingId ? "Update Report" : "Submit Report"}
            </Button>
            <Button type="button" data-testid="send-email-btn" variant="outline" disabled={!existingId} onClick={() => setChecklistOpen(true)}
              className="rounded-md border-border">
              <PaperPlaneTilt size={18} className="mr-1.5" /> Send to Email
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/reports")} className="rounded-md">
              View history
            </Button>
          </div>
        </form>
      </Card>

      <SendChecklistDialog open={checklistOpen} onOpenChange={setChecklistOpen} onConfirm={sendEmail} sending={sending} />
    </div>
  );
}
